import PaginationButtons from '../Common/Pagination';
import { BookModel } from '../../interfaces/book.interface';
import { calculatingTimeFromTo, convertTimestampToDateTimeString } from '../../helper/helper';
import { SecureImg } from '../Common/SecureImage';
import { Toast } from '../Common/Toast';
import Checkbox from '../Common/Checkbox';
import BookCompleteBadge from '../BookCompleteBadge';

// Available-to-ship count for a book (purchased but not yet shipped).
const purchasedToShip = (book: BookModel): number =>
  (Number(book?.purchasedCounter) || 0) - (Number(book?.shippedCounter) || 0);

const copyBookId = (bookId: string) => {
  if (!bookId) return;
  if (navigator.clipboard?.writeText) {
    navigator.clipboard.writeText(bookId).then(
      () => Toast(`Copied: ${bookId}`, false),
      () => Toast('Copy failed', true),
    );
  } else {
    Toast('Clipboard not available', true);
  }
};

interface AdminBookGridProps {
  books: BookModel[];
  totalBooksPages?: number;
  currentPage: number;
  handleBooksPageChange: (page: string | number) => void;
  onOpenBookPage: (bookId: string) => void;
  onOpenFlipBook: (bookId: string) => void;
  // bookId -> number of book-print purchases. Only rendered when provided by the caller.
  purchaseCounts?: Record<string, number>;
  // Opt-in selection mode (used by the admin bulk "mark as purchased" flow).
  selectable?: boolean;
  selectedBookIds?: Set<string>;
  onToggleSelect?: (bookId: string) => void;
}

const AdminBookGrid: React.FC<AdminBookGridProps> = ({
  books,
  totalBooksPages,
  currentPage,
  handleBooksPageChange,
  onOpenBookPage,
  onOpenFlipBook,
  purchaseCounts,
  selectable,
  selectedBookIds,
  onToggleSelect,
}) => {
  return (
    <div>
      <div className="w-full rounded-md flex justify-center overflow-y-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {books &&
            books.map((book: BookModel) => {
              const pagesMap: any = book?.pages ?? {};
              const imagesGenerated = Object.keys(pagesMap).reduce(
                (sum: number, key: string) => sum + (pagesMap[key]?.images?.length ?? 0),
                0,
              );
              const toShip = purchasedToShip(book);
              const isSelected = !!(book?.bookId && selectedBookIds?.has(book.bookId));
              return (
              <div
                key={book?.id!}
                className={`p-2 book-item font-sans text-white bg-storytan uppercase text-2xl font-bold max-w-[19rem] rounded-md mx-auto mb-2 ${
                  selectable && isSelected ? 'ring-2 ring-inset ring-storygreen' : ''
                }`}
              >
                <p className="flex text-[11px] text-storyblue capitalize">
                  Generation time:{' '}
                  {calculatingTimeFromTo(
                    convertTimestampToDateTimeString(book?.timestamp, true),
                    convertTimestampToDateTimeString(book?.fullBookCompletedAt, true),
                  )}
                </p>
                <div className="relative flex justify-center">
                  {selectable && book?.bookId && (
                    <Checkbox
                      checked={isSelected}
                      onCheckedChange={() => onToggleSelect?.(book.bookId!)}
                      className="!absolute top-2 left-2 z-10 shadow-md"
                      checkedBackgroundColor="storygreen"
                      title={isSelected ? 'Deselect' : 'Select'}
                    />
                  )}
                  {toShip > 0 && (
                    <span
                      className="absolute top-2 right-2 z-10 rounded-full bg-storygreen text-white text-[10px] font-bold normal-case px-2 py-1 shadow-md"
                      title="Purchased and available for the teacher to ship"
                    >
                      Purchased ×{toShip}
                    </span>
                  )}
                  <SecureImg
                    storageSrc={book?.coverURL || 'bookdraft.png'}
                    alt={`${book?.title} cover`}
                    className="h-[18rem] w-[18rem] object-cover rounded-md shadow-xl mb-2"
                  />
                  <BookCompleteBadge book={book} className="absolute bottom-3 left-2 z-10 shadow-md" />
                </div>
                <button
                  type="button"
                  onClick={() => onOpenBookPage(book.bookId!)}
                  className="text-storytan text-sm h-10 w-full flex items-center justify-center rounded-md bg-storyblue shadow-xl leading-none font-bold font-serif p-4 cursor-pointer hover:opacity-90"
                  title="Open book"
                >
                  {book.title || 'Draft'}
                </button>
                {book?.bookId ? (
                  <button
                    type="button"
                    onClick={() => copyBookId(book.bookId!)}
                    className="text-[11px] text-storyblue capitalize hover:underline cursor-pointer text-left w-full"
                    title="Click to copy"
                  >
                    Book Id: {book.bookId}
                  </button>
                ) : (
                  <p className="text-[11px] text-storyblue capitalize">Book Id Missing</p>
                )}
                <p className="text-[11px] text-storyblue capitalize">
                  Images generated: {imagesGenerated}
                </p>
                {purchaseCounts && (
                  <p className="text-[11px] text-storyblue capitalize">
                    Purchased: {(book?.bookId && purchaseCounts[book.bookId]) || 0}
                  </p>
                )}
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => onOpenBookPage(book.bookId!)}
                    className="w-full h-8 flex justify-center items-center pointer-grab text-white text-xs rounded-md bg-storyblueSecondLightText shadow-xl leading-none font-bold p-2 capitalize"
                  >
                    Page View
                  </button>
                  <button
                    onClick={() => onOpenFlipBook(book.id!)}
                    className="w-full h-8 flex justify-center items-center text-white text-xs bg-storyblueSecondLightText rounded-md shadow-xl leading-none font-bold p-2 capitalize"
                  >
                    FlipBook
                  </button>
                </div>
              </div>
              );
            })}
        </div>
      </div>
      <div className="flex justify-end mt-4 gap-2">
        {!!totalBooksPages && totalBooksPages > 1 && (
          <PaginationButtons
            totalPages={totalBooksPages}
            currentPage={currentPage}
            handlePageChange={handleBooksPageChange}
          />
        )}
      </div>
    </div>
  );
};

export default AdminBookGrid;
